import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Check, Code, Palette, Settings, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const MerchantWidgetGuide = () => {
  const { toast } = useToast();
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const baseUrl = window.location.origin;

  const basicInstallCode = `<!-- Shakatah Split Payment Widget -->
<script src="${baseUrl}/shakatah-widget.js" async></script>`;

  const customConfigCode = `<!-- Shakatah Configuration (optional) -->
<script>
  window.ShakatahConfig = {
    buttonText: "Split Payment with Friends",
    primaryColor: "#8B5CF6",
    buttonPosition: "after-add-to-cart"
  };
</script>
<script src="${baseUrl}/shakatah-widget.js" async></script>`;

  const advancedConfigCode = `<!-- Advanced Shakatah Configuration -->
<script>
  window.ShakatahConfig = {
    // Button text
    buttonText: "Split with Friends",
    
    // Button placement
    // Options: "after-add-to-cart", "before-add-to-cart", "custom"
    buttonPosition: "after-add-to-cart",
    
    // Custom selector (only used if buttonPosition is "custom")
    buttonSelector: "#my-custom-container",
    
    // Brand color (affects button gradient)
    primaryColor: "#8B5CF6",
    
    // Shakatah checkout URL (don't change unless testing)
    shakatahUrl: "${baseUrl}"
  };
</script>
<script src="${baseUrl}/shakatah-widget.js" async></script>`;

  const copyToClipboard = async (text: string, section: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast({
      title: "Copied!",
      description: "Code copied to clipboard",
    });
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const CodeBlock = ({ code, section }: { code: string; section: string }) => (
    <div className="relative">
      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
        <code className="text-foreground">{code}</code>
      </pre>
      <Button
        size="sm"
        variant="secondary"
        className="absolute top-2 right-2"
        onClick={() => copyToClipboard(code, section)}
      >
        {copiedSection === section ? (
          <Check className="h-4 w-4" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4" variant="secondary">Developer Guide</Badge>
          <h1 className="text-4xl font-bold mb-4">Install Shakatah Widget</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Add split payment functionality to your Shopify store in minutes
          </p>
        </div>

        {/* Quick Start */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Quick Start
            </CardTitle>
            <CardDescription>
              Add this single line to your Shopify theme to enable split payments
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CodeBlock code={basicInstallCode} section="basic" />
            
            <div className="bg-accent/50 rounded-lg p-4">
              <h4 className="font-medium mb-2">Installation Steps:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>Go to your Shopify Admin → <strong>Online Store</strong> → <strong>Themes</strong></li>
                <li>Click <strong>Actions</strong> → <strong>Edit code</strong></li>
                <li>Open <code className="bg-muted px-1 rounded">theme.liquid</code> file</li>
                <li>Paste the code above just before the closing <code className="bg-muted px-1 rounded">&lt;/body&gt;</code> tag</li>
                <li>Click <strong>Save</strong></li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Configuration Tabs */}
        <Tabs defaultValue="customize" className="mb-8">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="customize" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Customize
            </TabsTrigger>
            <TabsTrigger value="advanced" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Advanced
            </TabsTrigger>
          </TabsList>

          <TabsContent value="customize">
            <Card>
              <CardHeader>
                <CardTitle>Customize Appearance</CardTitle>
                <CardDescription>
                  Match the widget to your store's branding
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CodeBlock code={customConfigCode} section="custom" />
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">buttonText</h4>
                    <p className="text-sm text-muted-foreground">
                      The text displayed on the split payment button
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">primaryColor</h4>
                    <p className="text-sm text-muted-foreground">
                      Hex color for the button gradient (e.g., #8B5CF6)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="advanced">
            <Card>
              <CardHeader>
                <CardTitle>Advanced Configuration</CardTitle>
                <CardDescription>
                  Full control over widget behavior and placement
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CodeBlock code={advancedConfigCode} section="advanced" />
                
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">buttonPosition</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      Controls where the button appears relative to Add to Cart
                    </p>
                    <ul className="text-sm space-y-1 ml-4">
                      <li><code className="bg-muted px-1 rounded">"after-add-to-cart"</code> - Below the Add to Cart button (default)</li>
                      <li><code className="bg-muted px-1 rounded">"before-add-to-cart"</code> - Above the Add to Cart button</li>
                      <li><code className="bg-muted px-1 rounded">"custom"</code> - Use buttonSelector to specify location</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">buttonSelector</h4>
                    <p className="text-sm text-muted-foreground">
                      CSS selector for custom button placement. Only used when buttonPosition is "custom".
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* How it Works */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 sm:grid-cols-3">
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-primary">1</span>
                </div>
                <h4 className="font-medium mb-2">Customer Clicks</h4>
                <p className="text-sm text-muted-foreground">
                  Customer clicks "Split Payment" on your product page
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-primary">2</span>
                </div>
                <h4 className="font-medium mb-2">Add Friends</h4>
                <p className="text-sm text-muted-foreground">
                  They enter friend details and set a payment deadline
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-primary">3</span>
                </div>
                <h4 className="font-medium mb-2">Friends Pay</h4>
                <p className="text-sm text-muted-foreground">
                  Friends receive payment links and pay their share directly to you
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Support */}
        <Card>
          <CardContent className="py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-medium">Need help with installation?</h4>
                <p className="text-sm text-muted-foreground">
                  Our team is here to assist you
                </p>
              </div>
              <Button variant="outline" className="gap-2">
                <ExternalLink className="h-4 w-4" />
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MerchantWidgetGuide;
